import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';

export const QuoteSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    device_brand: '',
    device_model: '',
    issue_type: '',
    issue_description: '',
    urgency: '',
    pickup_required: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('/api/quote', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          device_brand: '',
          device_model: '',
          issue_type: '',
          issue_description: '',
          urgency: '',
          pickup_required: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-br from-red-50 to-red-100">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <i className="ri-price-tag-3-line text-red-600 text-2xl"></i>
              <span className="text-red-600 font-semibold text-lg">Get Free Quote</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Get Instant Repair Quote
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and get a free, transparent quote for your device repair. 
              No hidden charges, just honest pricing.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form id="quote-form" onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Device Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="device_brand" className="block text-sm font-medium text-gray-700 mb-2">
                      Device Brand *
                    </label>
                    <select
                      id="device_brand"
                      name="device_brand"
                      value={formData.device_brand}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select brand</option>
                      <option value="Apple">Apple</option>
                      <option value="Samsung">Samsung</option>
                      <option value="OnePlus">OnePlus</option>
                      <option value="Google">Google Pixel</option>
                      <option value="Xiaomi">Xiaomi</option>
                      <option value="Vivo">Vivo</option>
                      <option value="Realme">Realme</option>
                      <option value="Oppo">Oppo</option>
                      <option value="Huawei">Huawei</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="device_model" className="block text-sm font-medium text-gray-700 mb-2">
                      Device Model *
                    </label>
                    <input
                      type="text"
                      id="device_model"
                      name="device_model"
                      value={formData.device_model}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="e.g., iPhone 14 Pro, Galaxy S23, OnePlus 11"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Issue Details</h3>
                <div className="mb-4">
                  <label htmlFor="issue_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Issue Type *
                  </label>
                  <select
                    id="issue_type"
                    name="issue_type"
                    value={formData.issue_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select issue type</option>
                    <option value="Screen Cracked/Broken">Screen Cracked/Broken</option>
                    <option value="Battery Issues">Battery Issues</option>
                    <option value="Charging Problems">Charging Problems</option>
                    <option value="Water Damage">Water Damage</option>
                    <option value="Camera Not Working">Camera Not Working</option>
                    <option value="Speaker/Audio Issues">Speaker/Audio Issues</option>
                    <option value="Software Problems">Software Problems</option>
                    <option value="Data Recovery">Data Recovery</option>
                    <option value="Physical Damage">Physical Damage</option>
                    <option value="Performance Issues">Performance Issues</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="issue_description" className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description *
                  </label>
                  <textarea
                    id="issue_description"
                    name="issue_description"
                    value={formData.issue_description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                    placeholder="Please describe the issue in detail. When did it start? What happened? Any specific symptoms? (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.issue_description.length}/500 characters</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency Level *
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select urgency</option>
                      <option value="Emergency (Same Day)">Emergency (Same Day)</option>
                      <option value="Urgent (Within 24 hours)">Urgent (Within 24 hours)</option>
                      <option value="Normal (2-3 days)">Normal (2-3 days)</option>
                      <option value="No Rush (When convenient)">No Rush (When convenient)</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pickup_required" className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Required? *
                    </label>
                    <select
                      id="pickup_required"
                      name="pickup_required"
                      value={formData.pickup_required}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select option</option>
                      <option value="Yes - Free Pickup & Delivery">Yes - Free Pickup & Delivery</option>
                      <option value="No - I'll visit the store">No - I'll visit the store</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">What happens next?</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• We'll review your request and call you within 30 minutes</li>
                      <li>• Free diagnosis and transparent quote provided</li>
                      <li>• If you choose pickup, we'll collect your device for free</li>
                      <li>• Repair completed with genuine parts and warranty</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || formData.issue_description.length > 500}
                className="w-full"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <i className="ri-price-tag-3-line mr-2"></i>
                    Get Free Quote
                  </>
                )}
              </Button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <i className="ri-check-circle-line text-green-600"></i>
                    <p className="text-green-800">Quote request submitted successfully! We'll call you within 30 minutes.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    <p className="text-red-800">Failed to submit request. Please try again or call us directly at +91 98765 43210.</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};